import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles, Wand2 } from 'lucide-react';
import type { TranslationKeys } from '@/core/i18n/translations';
import type { AIStepType, AIWorkoutResponse } from '@/types';
import type { PromptInspirationItem } from './types';

export interface ExploreTabProps {
  t: TranslationKeys;
  onUseTemplate: (item: PromptInspirationItem) => void;
  /** Optional CTA — when supplied, the empty state encourages the user to jump into the builder. */
  onGoToBuilder?: () => void;
}

type ApiPayload = {
  items?: PromptInspirationItem[];
  error?: string;
  /** Optional diagnostic reason emitted by the backend when the catalog is empty. */
  reason?: string;
};

function stepHue(type: AIStepType): string {
  switch (type) {
    case 'warmup':
      return 'bg-emerald-400 dark:bg-emerald-500';
    case 'cooldown':
      return 'bg-violet-400 dark:bg-violet-500';
    case 'interval':
      return 'bg-orange-500 dark:bg-orange-400';
    default:
      return 'bg-sky-500 dark:bg-sky-400';
  }
}

function stepMinutes(work: { duration: number; unit: string }): number {
  if (work.unit === 'min') return Math.max(0.1, work.duration);
  if (work.unit === 'sec') return Math.max(0.1, work.duration / 60);
  return 3;
}

function InspirationPhaseStrip({ workout }: { workout: AIWorkoutResponse | null }) {
  if (!workout?.steps?.length) {
    return <div className="h-3 w-full rounded-full bg-slate-200 dark:bg-slate-700" aria-hidden />;
  }
  const bands = workout.steps.map((step) => ({
    minutes: stepMinutes(step.work),
    hue: stepHue(step.type),
  }));
  const total = bands.reduce((s, b) => s + b.minutes, 0) || 1;
  return (
    <div
      className="flex h-3 w-full gap-0.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"
      role="img"
      aria-label={workout.workoutName ?? 'Workout profile'}
    >
      {bands.map((b, i) => (
        <div
          key={i}
          className={`${b.hue} h-full min-w-[3px] rounded-sm transition-all`}
          style={{ flexGrow: b.minutes / total }}
        />
      ))}
    </div>
  );
}

function responseLooksLikeViteSpaFallback(res: Response, raw: string): boolean {
  if (import.meta.env.PROD) return false;
  const head = raw.trimStart();
  if (res.status === 404) return true;
  if (head.startsWith('<!') || head.toLowerCase().startsWith('<html')) return true;
  return false;
}

export function ExploreTab({ t, onUseTemplate, onGoToBuilder }: ExploreTabProps) {
  const [items, setItems] = useState<PromptInspirationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [devFunctionsUnavailable, setDevFunctionsUnavailable] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    setDevFunctionsUnavailable(false);
    try {
      const res = await fetch('/.netlify/functions/get-inspirations');
      const raw = await res.text();
      if (responseLooksLikeViteSpaFallback(res, raw)) {
        console.warn(
          '[ExploreTab] get-inspirations: HTML or 404 from dev server — Netlify functions not mounted (use npx netlify dev, not only npm run dev).',
        );
        setItems([]);
        setDevFunctionsUnavailable(true);
        return;
      }
      let body: ApiPayload = {};
      try {
        body = JSON.parse(raw) as ApiPayload;
      } catch (parseErr) {
        console.error('[ExploreTab] Invalid JSON from get-inspirations', { status: res.status, parseErr });
        throw new Error('INVALID_JSON');
      }
      if (!res.ok) {
        console.error('[ExploreTab] get-inspirations HTTP error', {
          status: res.status,
          error: body.error,
          details: 'details' in body ? (body as { details?: string }).details : undefined,
        });
        throw new Error(body.error ?? 'REQUEST_FAILED');
      }
      if (body.reason) {
        // Backend signalled a soft fallback (missing config / pending migration).
        // We still treat the response as a clean empty state instead of a red error.
        console.warn('[ExploreTab] get-inspirations returned empty catalog', { reason: body.reason });
      }
      setItems(Array.isArray(body.items) ? body.items : []);
    } catch (e) {
      console.error('[ExploreTab] load inspirations failed', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <section
        className="bg-white p-6 rounded-2xl border border-blue-200 shadow-xl backdrop-filter backdrop-blur-lg bg-opacity-80 dark:bg-slate-900 dark:border-slate-700 dark:bg-opacity-10"
        aria-busy="true"
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">{t.exploreLoading}</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="bg-white p-6 rounded-2xl border border-red-200 shadow-xl dark:bg-slate-900 dark:border-red-900/50">
        <p className="text-sm text-red-700 dark:text-red-300">{t.exploreError}</p>
        <button
          type="button"
          onClick={() => void load()}
          className="mt-4 min-h-[44px] px-4 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 touch-manipulation"
        >
          {t.exploreRetry}
        </button>
      </section>
    );
  }

  if (devFunctionsUnavailable && !items.length) {
    return (
      <section
        data-testid="explore-dev-functions-hint"
        className="bg-white p-6 rounded-2xl border border-amber-300 shadow-xl dark:bg-slate-900 dark:border-amber-700/60"
      >
        <h2 className="font-heading text-lg font-semibold text-amber-950 dark:text-amber-100 mb-2">{t.exploreDevFunctionsTitle}</h2>
        <p className="text-sm text-amber-950/90 dark:text-amber-100/90 leading-relaxed">{t.exploreDevFunctionsBody}</p>
        <button
          type="button"
          onClick={() => void load()}
          className="mt-4 min-h-[44px] px-4 rounded-lg bg-amber-700 text-white text-sm font-medium hover:bg-amber-600 touch-manipulation"
        >
          {t.exploreRetry}
        </button>
        {onGoToBuilder ? (
          <button
            type="button"
            onClick={onGoToBuilder}
            className="mt-3 block w-full min-h-[44px] px-4 rounded-lg border border-amber-600 text-amber-900 dark:text-amber-100 text-sm font-medium hover:bg-amber-50 dark:hover:bg-amber-950/40 touch-manipulation"
          >
            {t.exploreEmptyCta}
          </button>
        ) : null}
      </section>
    );
  }

  if (!items.length) {
    return (
      <section
        data-testid="explore-empty-state"
        className="bg-white p-8 rounded-2xl border border-blue-200 shadow-xl backdrop-filter backdrop-blur-lg bg-opacity-80 dark:bg-slate-900 dark:border-slate-700 dark:bg-opacity-20 text-center"
      >
        <div className="mx-auto inline-flex items-center justify-center w-14 h-14 rounded-full bg-orange-100 dark:bg-orange-900/40 mb-4">
          <Sparkles className="w-7 h-7 text-orange-500 dark:text-orange-300" aria-hidden />
        </div>
        <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
          {t.exploreEmptyTitle}
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-300 max-w-md mx-auto leading-relaxed mb-5">
          {t.exploreEmpty}
        </p>
        {onGoToBuilder ? (
          <button
            type="button"
            data-testid="explore-empty-go-builder"
            onClick={onGoToBuilder}
            className="inline-flex items-center gap-2 min-h-[44px] px-5 rounded-xl bg-orange-600 text-white text-sm font-semibold hover:bg-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900 touch-manipulation transition-colors"
          >
            <Wand2 className="w-4 h-4 shrink-0" aria-hidden />
            {t.exploreEmptyCta}
          </button>
        ) : null}
      </section>
    );
  }

  return (
    <section className="bg-white p-6 rounded-2xl border border-blue-200 shadow-xl backdrop-filter backdrop-blur-lg bg-opacity-80 dark:bg-slate-900 dark:border-slate-700 dark:bg-opacity-10">
      <div className="flex items-start gap-3 mb-4">
        <Sparkles className="w-6 h-6 text-orange-500 shrink-0 mt-0.5" aria-hidden />
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100">{t.exploreTitle}</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{t.exploreSubtitle}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items.map((item) => (
          <article
            key={item.id}
            className="flex flex-col gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-950/40"
          >
            <InspirationPhaseStrip workout={item.output_json} />
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <span className="rounded-full bg-slate-200 dark:bg-slate-800 px-2 py-0.5 font-medium uppercase tracking-wide">
                {t.exploreLangBadge}: {item.language}
              </span>
              <span className="rounded-full bg-blue-100 dark:bg-blue-950/60 text-blue-800 dark:text-blue-200 px-2 py-0.5 font-medium">
                {item.sport === 'run' ? t.exploreSportRun : t.exploreSportBike}
              </span>
            </div>
            <p className="text-sm text-slate-800 dark:text-slate-200 leading-snug line-clamp-4">{item.prompt_text}</p>
            <button
              type="button"
              data-testid={`explore-use-template-${item.id}`}
              onClick={() => onUseTemplate(item)}
              className="mt-auto min-h-[44px] w-full rounded-lg bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900 touch-manipulation"
            >
              {t.exploreUseTemplate}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}
